import { useEffect, useState } from "react";
import api, { fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useWorkerJob, jobLabel } from "@/lib/workerJob";

const errText = (e, f) => e?.response?.data?.detail && typeof e.response.data.detail === "string" ? e.response.data.detail : f;
const dateOf = (iso) => iso ? new Date(iso).toLocaleDateString("en-NZ", { timeZone: "Pacific/Auckland", day: "2-digit", month: "2-digit", year: "numeric" }) : "";
const timeOf = (iso) => iso ? new Date(iso).toLocaleTimeString("en-NZ", { timeZone: "Pacific/Auckland", hour: "2-digit", minute: "2-digit" }) : "";

function SiteSign({ pid }) {
  const [st, setSt] = useState(null);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setSt(null); api.get(`/my/projects/${pid}/site-status`).then(r => setSt(r.data)).catch(() => setSt({ signed_in: false })); }, [pid]);
  async function act(action) {
    setBusy(true); setMsg("");
    try {
      const { data } = await api.post(`/my/projects/${pid}/site-sign`, { action });
      setSt({ signed_in: action === "sign-in", at: data.at });
      setMsg(action === "sign-in" ? "Signed in — have a safe shift." : "Signed out — thanks.");
    } catch (e) { setMsg(errText(e, "Failed")); }
    finally { setBusy(false); }
  }
  return (
    <div className="card space-y-2" data-testid="worker-site-sign">
      <h2 className="text-silver">Site sign-in</h2>
      <div className="text-sm" data-testid="worker-site-status">
        {st === null ? "…" : st.signed_in ? `Signed in since ${timeOf(st.at)} (${dateOf(st.at)})` : "Not signed in"}
      </div>
      <div className="flex gap-2">
        <button className="btn-silver" disabled={busy || st?.signed_in} onClick={() => act("sign-in")} data-testid="worker-sign-in">Sign in</button>
        <button className="btn-ghost" disabled={busy || !st?.signed_in} onClick={() => act("sign-out")} data-testid="worker-sign-out">Sign out</button>
      </div>
      {msg && <div className="text-xs text-silver" data-testid="worker-site-msg">{msg}</div>}
      <div className="text-xs opacity-60">You can also scan the site QR poster with your phone camera.</div>
    </div>
  );
}

function Toolbox({ pid }) {
  const { user } = useAuth();
  const [talks, setTalks] = useState([]);
  const [wid, setWid] = useState("");
  const [msg, setMsg] = useState("");
  const load = () => api.get("/toolbox-talks", { params: { project_id: pid } }).then(r => setTalks(r.data));
  useEffect(() => { load(); api.get("/me/onboarding").then(r => setWid(r.data.worker_id || "")); }, [pid]); // eslint-disable-line react-hooks/exhaustive-deps
  async function sign(t) {
    if (!window.confirm(`Sign off "${t.topic}" as ${user?.name}?`)) return;
    try { await api.post(`/toolbox-talks/${t.id}/sign`, { worker_id: wid, signature_data: `typed:${user?.name}` }); setMsg(""); load(); }
    catch (e) { setMsg(errText(e, "Sign-off failed")); }
  }
  return (
    <div className="card space-y-2" data-testid="worker-toolbox">
      <h2 className="text-silver">Toolbox talks</h2>
      {talks.length === 0 && <div className="text-sm opacity-60" data-testid="worker-no-toolbox">No toolbox talks on this project yet.</div>}
      <ul className="space-y-2">{talks.map(t => {
        const mine = (t.signatures || []).find(s => s.worker_id === wid);
        return (
          <li key={t.id} className="border border-neutral-800 rounded px-3 py-2 flex items-center justify-between gap-2 flex-wrap" data-testid={`worker-toolbox-${t.id}`}>
            <div><div>{t.topic}</div><div className="text-xs opacity-60">{fmtDate(t.created_at)}{t.conducted_by ? ` · ${t.conducted_by}` : ""}</div></div>
            {mine ? <span className="text-xs text-green-400" data-testid={`worker-toolbox-signed-${t.id}`}>Signed {fmtDate(mine.at)}</span>
                  : <button className="btn-silver text-sm" onClick={() => sign(t)} data-testid={`worker-toolbox-sign-${t.id}`}>Sign off</button>}
          </li>
        );
      })}</ul>
      {msg && <div className="text-xs text-red-400">{msg}</div>}
    </div>
  );
}

function ReportForm({ pid, onClose }) {
  const [f, setF] = useState({ kind: "hazard", description: "", location: "", risk_level: "Medium" });
  const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);
  async function submit(e) {
    e.preventDefault();
    if (f.description.trim().length < 3) { setErr("Describe what you saw."); return; }
    setBusy(true); setErr("");
    try { await api.post("/my/hs/report", { ...f, project_id: pid }); onClose(f.kind === "hazard" ? "Hazard reported — thanks. The office will review it." : "Incident reported — thanks. The office will review it."); }
    catch (e2) { setErr(errText(e2, "Report failed")); setBusy(false); }
  }
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <form onSubmit={submit} className="card space-y-2" data-testid="worker-report-form">
      <h2 className="text-silver">Report a hazard or incident</h2>
      <select className="input" value={f.kind} onChange={set("kind")} data-testid="worker-report-kind">
        <option value="hazard">Hazard</option><option value="incident">Incident</option>
      </select>
      <textarea className="input min-h-[90px]" placeholder="What happened / what's the hazard?" value={f.description} onChange={set("description")} data-testid="worker-report-description"/>
      <input className="input" placeholder="Where on site (optional)" value={f.location} onChange={set("location")} data-testid="worker-report-location"/>
      <select className="input" value={f.risk_level} onChange={set("risk_level")} data-testid="worker-report-risk">
        {["Low", "Medium", "High", "Critical"].map(r => <option key={r}>{r}</option>)}
      </select>
      {err && <div className="text-xs text-red-400" data-testid="worker-report-error">{err}</div>}
      <div className="flex gap-2">
        <button className="btn-silver" disabled={busy} data-testid="worker-report-submit">{busy ? "Sending…" : "Send report"}</button>
        <button type="button" className="btn-ghost" onClick={() => onClose("")} data-testid="worker-report-cancel">Cancel</button>
      </div>
    </form>
  );
}

export default function WorkerHs() {
  const [projects, setProjects] = useState(null);
  const [job, setJob] = useWorkerJob();
  const [reporting, setReporting] = useState(false);
  const [done, setDone] = useState("");
  useEffect(() => { api.get("/my/projects").then(r => setProjects(r.data)).catch(() => setProjects([])); }, []);
  const valid = !!projects?.some(p => p.id === job);

  return (
    <div className="space-y-4" data-testid="worker-hs">
      <h1 className="text-2xl">Health &amp; Safety</h1>
      <select className="input" value={valid ? job : ""} onChange={e => { setJob(e.target.value); setReporting(false); setDone(""); }} data-testid="worker-hs-project">
        <option value="">— Pick a project —</option>
        {(projects || []).map(p => <option key={p.id} value={p.id}>{jobLabel(p)}</option>)}
      </select>
      {projects?.length === 0 && <div className="text-sm opacity-60">There are no open projects right now.</div>}
      {valid && (
        <>
          <SiteSign pid={job} />
          <Toolbox pid={job} />
          {done && <div className="card text-sm text-silver" data-testid="worker-report-done">{done}</div>}
          {reporting
            ? <ReportForm pid={job} onClose={(m) => { setReporting(false); setDone(m); }} />
            : <button className="btn-silver w-full py-3" onClick={() => { setReporting(true); setDone(""); }} data-testid="worker-report-btn">Report a hazard or incident</button>}
        </>
      )}
    </div>
  );
}
